import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { studentsService, type StudentDto } from '@/services/students';
import { authService, type TeacherInvitationDto } from '@/services/auth';
import { tenantService, type TenantDto } from '@/services/tenants';
import { Users, RefreshCw } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const SuperadminTenantUsersPage: React.FC = () => {
  const { toast } = useToast();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId')
  );
  const [selectedTenant, setSelectedTenant] = useState<TenantDto | null>(null);
  const [students, setStudents] = useState<StudentDto[]>([]);
  const [teacherInvitations, setTeacherInvitations] = useState<TeacherInvitationDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const tenantId = sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId');
    setSelectedTenantId(tenantId);
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;
    loadUsers(selectedTenantId);
  }, [selectedTenantId]);

  const loadUsers = async (tenantId: string) => {
    try {
      setLoading(true);
      const [tenant, studentsData, invitations] = await Promise.all([
        tenantService.getTenant(tenantId),
        studentsService.getStudentsByTenant(tenantId),
        authService.getTeacherInvitations(tenantId),
      ]);
      setSelectedTenant(tenant);
      setStudents(Array.isArray(studentsData) ? studentsData : []);
      setTeacherInvitations(Array.isArray(invitations) ? invitations : []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load tenant users',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tenant Users - Arrangelly Dashboard</title>
      </Helmet>
      <DashboardLayout>
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          <Card className=" mb-6">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Users className="w-5 h-5" />
                Users
              </CardTitle>
              <CardDescription>
                {selectedTenant
                  ? `${selectedTenant.name}: students and teacher invitation status`
                  : 'Select a tenant in Tenants page to inspect users'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedTenantId ? (
                <p className="text-gray-400">No tenant selected. Go to Tenants and choose one.</p>
              ) : loading ? (
                <p className="text-gray-400">Loading users...</p>
              ) : (
                <div className="space-y-6">
                  <div className="flex justify-start sm:justify-end">
                    <Button
                      variant="outline"
                      className="border-white/20"
                      onClick={() => selectedTenantId && loadUsers(selectedTenantId)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>

                  <div>
                    <h3 className="text-foreground font-semibold mb-2">Students ({students.length})</h3>
                    {students.length === 0 ? (
                      <p className="text-gray-400 text-sm">No students found.</p>
                    ) : (
                      <div className="space-y-2">
                        {students.map((student) => (
                          <div
                            key={student.id}
                            className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                          >
                            <div>
                              <p className="text-foreground text-sm font-medium">
                                {[student.firstName, student.lastName].filter(Boolean).join(' ') || 'Unnamed Student'}
                              </p>
                              <p className="text-xs text-gray-400">{student.email || 'No email available'}</p>
                            </div>
                            <p className="text-xs text-gray-500">
                              {new Date(student.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-foreground font-semibold mb-2">
                      Teacher Invitations ({teacherInvitations.length})
                    </h3>
                    {teacherInvitations.length === 0 ? (
                      <p className="text-gray-400 text-sm">No teacher invitations yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {teacherInvitations.map((invitation) => (
                          <div
                            key={invitation.id}
                            className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                          >
                            <div>
                              <p className="text-foreground text-sm">{invitation.email}</p>
                              <p className="text-xs text-gray-400">
                                Created: {new Date(invitation.createdAt).toLocaleString()}
                              </p>
                            </div>
                            <span className="text-xs px-2 py-1 rounded border border-white/20 text-gray-300">
                              {invitation.status.toUpperCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default SuperadminTenantUsersPage;
